import React, { useMemo } from 'react'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CWidgetStatsA,
} from '@coreui/react-pro'
import { CChart } from '@coreui/react-chartjs'
import { useChartTheme, baseChartOptions, axis, PLATFORM_META } from './chartTheme'
import {
  ChartSkeleton,
  EmptyState,
  ErrorState,
  TileSkeleton,
  capWithOther,
  computeDelta,
  formatDateLabel,
  formatDecimal,
  formatNumber,
} from './common'

/**
 * KPI tiles. Each carries its number, the change against the previous period of
 * equal length, and a sparkline of the same metric — a bare count is not
 * interpretable on its own.
 */
const KPI_TILES = [
  { key: 'sessions', label: 'App sessions', color: 'primary', format: formatNumber },
  { key: 'active_users', label: 'People who opened the app', color: 'info', format: formatNumber },
  { key: 'screen_views', label: 'Screens viewed', color: 'success', format: formatNumber },
  { key: 'actions', label: 'Taps & shares', color: 'warning', format: formatNumber },
  {
    key: 'avg_screens_per_session',
    label: 'Screens per session',
    color: 'secondary',
    format: (v) => formatDecimal(v, 2),
  },
  {
    key: 'avg_session_minutes',
    label: 'Average time in app',
    color: 'dark',
    format: (v) => `${formatDecimal(v, 1)} min`,
  },
]

const Sparkline = ({ points, theme }) => {
  if (!points || points.length < 2) return null

  return (
    <CChart
      type="line"
      className="mt-3 mx-3"
      style={{ height: '70px' }}
      data={{
        labels: points.map((_, i) => i),
        datasets: [
          {
            label: 'Trend',
            data: points,
            borderColor: 'rgba(255,255,255,0.75)',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.4,
          },
        ],
      }}
      options={{
        maintainAspectRatio: false,
        responsive: true,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } },
        elements: { line: { borderCapStyle: 'round', borderJoinStyle: 'round' } },
      }}
    />
  )
}

const KpiRow = ({ summary, sparkSeries, theme, loading, error, onRetry }) => {
  if (loading) {
    return (
      <CRow>
        {KPI_TILES.map((tile) => (
          <CCol key={tile.key} xs={12} sm={6} xl={4} xxl={2}>
            <TileSkeleton />
          </CCol>
        ))}
      </CRow>
    )
  }

  if (error) {
    return (
      <CCard className="mb-4">
        <CCardBody>
          <ErrorState message={error} onRetry={onRetry} height={160} />
        </CCardBody>
      </CCard>
    )
  }

  const current = summary?.current || {}
  const previous = summary?.previous || {}

  return (
    <CRow>
      {KPI_TILES.map((tile) => {
        const delta = computeDelta(current[tile.key], previous[tile.key])
        return (
          <CCol key={tile.key} xs={12} sm={6} xl={4} xxl={2}>
            <CWidgetStatsA
              className="mb-4"
              color={tile.color}
              value={
                <>
                  {tile.format(current[tile.key])}
                  <span className="fs-6 fw-normal ms-2 av2-delta" title="vs previous period">
                    {delta.label}
                  </span>
                </>
              }
              title={tile.label}
              chart={<Sparkline points={sparkSeries?.[tile.key]} theme={theme} />}
            />
          </CCol>
        )
      })}
    </CRow>
  )
}

/** Trend over time — a grouped line, never a pie. iOS and Android as two labelled series. */
const SessionsTrend = ({ series, interval, theme, loading, error, onRetry }) => {
  const hasData = Array.isArray(series) && series.some((row) => Number(row.total) > 0)

  const data = useMemo(() => {
    if (!series) return { labels: [], datasets: [] }
    return {
      labels: series.map((row) => formatDateLabel(row.bucket)),
      datasets: PLATFORM_META.map((platform) => ({
        label: platform.label,
        data: series.map((row) => Number(row[platform.key]) || 0),
        borderColor: theme.series[platform.slot],
        backgroundColor: theme.series[platform.slot],
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: theme.surface,
        tension: 0.3,
      })),
    }
  }, [series, theme])

  return (
    <CCard className="mb-4">
      <CCardHeader>
        <strong>App sessions over time</strong>
        <div className="small text-body-secondary">
          Counted on the {interval === 'week' ? 'week' : 'day'} the session started, in the
          game&apos;s timezone
        </div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={320} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={320} />}
        {!loading && !error && !hasData && <EmptyState height={320} />}
        {!loading && !error && hasData && (
          <div style={{ height: 320 }}>
            <CChart
              type="line"
              data={data}
              options={baseChartOptions(theme, {
                scales: {
                  x: axis(theme, { grid: { display: false } }),
                  y: axis(theme, {
                    beginAtZero: true,
                    ticks: { color: theme.textMuted, precision: 0 },
                  }),
                },
              })}
            />
          </div>
        )}
      </CCardBody>
    </CCard>
  )
}

/** Parts of a whole, two categories — a donut is the right form here. */
const PlatformSplit = ({ platforms, theme, loading, error, onRetry }) => {
  const rows = (platforms || []).filter((row) => Number(row.sessions) > 0)
  const hasData = rows.length > 0

  const colorFor = (deviceType) => {
    const meta = PLATFORM_META.find((p) => p.key === deviceType)
    return meta ? theme.series[meta.slot] : theme.neutral
  }

  const labelFor = (deviceType) => {
    const meta = PLATFORM_META.find((p) => p.key === deviceType)
    return meta ? meta.label : 'Unknown device'
  }

  return (
    <CCard className="mb-4">
      <CCardHeader>
        <strong>iPhone vs Android</strong>
        <div className="small text-body-secondary">Share of sessions</div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={260} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={260} />}
        {!loading && !error && !hasData && <EmptyState height={260} />}
        {!loading && !error && hasData && (
          <>
            <div style={{ height: 240 }}>
              <CChart
                type="doughnut"
                data={{
                  labels: rows.map((row) => labelFor(row.device_type)),
                  datasets: [
                    {
                      data: rows.map((row) => Number(row.sessions)),
                      backgroundColor: rows.map((row) => colorFor(row.device_type)),
                      // A 2px surface-coloured gap does the separating, not a stroke.
                      borderColor: theme.surface,
                      borderWidth: 2,
                      hoverBorderColor: theme.surface,
                    },
                  ],
                }}
                options={baseChartOptions(theme, { cutout: '62%' })}
              />
            </div>
            {/* The numbers repeated as text, so identity never rests on colour. */}
            <CTable small borderless className="mt-3 mb-0 align-middle">
              <CTableBody>
                {rows.map((row) => (
                  <CTableRow key={row.device_type}>
                    <CTableDataCell style={{ width: 16 }}>
                      <span
                        className="av2-swatch"
                        style={{ background: colorFor(row.device_type) }}
                        aria-hidden="true"
                      />
                    </CTableDataCell>
                    <CTableDataCell>{labelFor(row.device_type)}</CTableDataCell>
                    <CTableDataCell className="text-end">
                      {formatNumber(row.sessions)}
                    </CTableDataCell>
                    <CTableDataCell className="text-end text-body-secondary">
                      {formatDecimal(row.share_pct, 1)}%
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

/** Version adoption — one stacked bar showing what share each build holds. */
const VersionAdoption = ({ versions, theme, loading, error, onRetry }) => {
  const capped = capWithOther(
    (versions || []).filter((row) => Number(row.sessions) > 0),
    5,
    ['sessions', 'users'],
    'app_version',
  )
  const hasData = capped.length > 0

  const colorFor = (index, row) =>
    row.__isOther ? theme.neutral : theme.series[index % theme.series.length]

  return (
    <CCard className="mb-4">
      <CCardHeader>
        <strong>Has the new build rolled out?</strong>
        <div className="small text-body-secondary">Share of sessions by app version</div>
      </CCardHeader>
      <CCardBody>
        {loading && <ChartSkeleton height={160} />}
        {!loading && error && <ErrorState message={error} onRetry={onRetry} height={160} />}
        {!loading && !error && !hasData && <EmptyState height={160} />}
        {!loading && !error && hasData && (
          <>
            <div style={{ height: 110 }}>
              <CChart
                type="bar"
                data={{
                  labels: ['Sessions'],
                  datasets: capped.map((row, index) => ({
                    label: row.__isOther
                      ? row.app_version
                      : `${row.app_version}${row.build_number ? ` (${row.build_number})` : ''}`,
                    data: [Number(row.sessions)],
                    backgroundColor: colorFor(index, row),
                    borderColor: theme.surface,
                    borderWidth: 2,
                    borderRadius: 4,
                    barThickness: 24,
                  })),
                }}
                options={baseChartOptions(theme, {
                  indexAxis: 'y',
                  scales: {
                    x: axis(theme, { stacked: true, display: false }),
                    y: axis(theme, { stacked: true, display: false, grid: { display: false } }),
                  },
                })}
              />
            </div>
            {/* Relief for the lighter palette slots: every value is also readable as text. */}
            <CTable small borderless responsive className="mt-2 mb-0 align-middle">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell colSpan={2}>Version</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Sessions</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">People</CTableHeaderCell>
                  <CTableHeaderCell className="text-end">Share</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {capped.map((row, index) => (
                  <CTableRow key={`${row.app_version}-${row.build_number || index}`}>
                    <CTableDataCell style={{ width: 16 }}>
                      <span
                        className="av2-swatch"
                        style={{ background: colorFor(index, row) }}
                        aria-hidden="true"
                      />
                    </CTableDataCell>
                    <CTableDataCell>
                      {row.app_version}
                      {row.build_number && (
                        <span className="text-body-secondary small ms-1">
                          build {row.build_number}
                        </span>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      {formatNumber(row.sessions)}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">{formatNumber(row.users)}</CTableDataCell>
                    <CTableDataCell className="text-end text-body-secondary">
                      {row.share_pct !== undefined ? `${formatDecimal(row.share_pct, 1)}%` : '—'}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

const OverviewTab = ({
  summary,
  timeseries,
  seriesByMetric,
  platforms,
  versions,
  interval,
  loading,
  errors,
  onRetry,
}) => {
  const theme = useChartTheme()

  // Sparklines are drawn only where a genuine per-bucket series exists.
  //
  // /timeseries supports sessions, screen_views and active_users, so those three
  // tiles get real trend lines, and screens-per-session is derived honestly from
  // two of them. `actions` and `avg_session_minutes` have no per-bucket series —
  // those tiles show the number and its delta with no sparkline, rather than
  // borrowing another metric's shape and implying something untrue.
  const sparkSeries = useMemo(() => {
    const totals = (key) => (seriesByMetric?.[key] || []).map((row) => Number(row.total) || 0)

    const sessions = totals('sessions')
    const screenViews = totals('screen_views')

    const perSession =
      sessions.length && sessions.length === screenViews.length
        ? sessions.map((s, i) => (s > 0 ? screenViews[i] / s : 0))
        : []

    return {
      sessions,
      screen_views: screenViews,
      active_users: totals('active_users'),
      avg_screens_per_session: perSession,
    }
  }, [seriesByMetric])

  return (
    <>
      <KpiRow
        summary={summary}
        sparkSeries={sparkSeries}
        theme={theme}
        loading={loading.summary}
        error={errors.summary}
        onRetry={onRetry}
      />

      <CRow>
        <CCol xs={12} xl={8}>
          <SessionsTrend
            series={timeseries}
            interval={interval}
            theme={theme}
            loading={loading.timeseries}
            error={errors.timeseries}
            onRetry={onRetry}
          />
        </CCol>
        <CCol xs={12} xl={4}>
          <PlatformSplit
            platforms={platforms}
            theme={theme}
            loading={loading.platforms}
            error={errors.platforms}
            onRetry={onRetry}
          />
        </CCol>
      </CRow>

      <VersionAdoption
        versions={versions}
        theme={theme}
        loading={loading.versions}
        error={errors.versions}
        onRetry={onRetry}
      />
    </>
  )
}

export default OverviewTab
